import { Component, EventEmitter, Input, OnDestroy, Output, ViewChildren, QueryList } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatSidecarService } from '../../services/chat-sidecar.service';
import { ChatApiService } from '../../services/chat-api.service';
import { ConversationStateService } from '../../services/conversation-state.service';
import { ArtifactStateService } from '../../services/artifact-state.service';
import { Message, Source } from '../../models/chat.model';
import { AiChatBotChatResponse } from '../../../../../../../../../goldstar-share/src/app/api-data/ng-openapi-gen-next/models';
import { ConversationSummary } from '../../models/conversation.model';
import { MessageWithFile } from '../../models/chat.model';
import { MessageComponent } from '../message/message.component';
import { UserInfoService } from '../../../../../../../../src/app/services/user-info.service';
import { S3FileService } from '../../../../../../../../../goldstar-share/src/app/services/s3file-service';
import { PermissionService } from '../../../../../../../../../goldstar-share/src/app/services/permission.service';

@Component({
	selector: 'app-chat-sidecar',
	templateUrl: './chat-sidecar.component.html',
	styleUrls: ['./chat-sidecar.component.scss'],
})
export class ChatSidecarComponent implements OnDestroy {
	// ── @ViewChild / @ViewChildren ──
	@ViewChildren(MessageComponent) messageComponents!: QueryList<MessageComponent>;

	// ── @Input / @Output ──
	@Input() isOpen = false;
	@Input() isFullscreen = false;
	@Output() openChange = new EventEmitter<boolean>();
	@Output() fullscreenChange = new EventEmitter<boolean>();

	public hasDirectModePermission = false;

	// ── State fields ──
	private openFreshSub: Subscription;
	internalUserGUID: string | undefined;
	messages: Message[] = [];
	chatMode: 'rag' | 'direct' = 'rag';
	isLoading = false;
	conversationId: string | undefined;
	errorMessage: string | null = null;
	selectedProjectId: string | undefined;
	fullscreenSidebarOpen = true;

	// ── Context menu state ──
	menuConvId: string | null = null;
	menuConvPinned = false;
	menuConvHasProject = false;
	menuConvProjectId: string | null = null;
	menuX = 0;
	menuY = 0;
	renameId: string | null = null;
	renameVal = '';

	// ── Arrow-function action bindings (preserve `this` for app-button [onClick]) ──
	readonly startNewChatAction = () => this.startNewChat();
	readonly toggleFullscreenAction = () => this.toggleFullscreen();
	readonly closeAction = () => this.close();
	readonly toggleFullscreenSidebarAction = () => this.toggleFullscreenSidebar();
	readonly setModeRagAction = () => this.setMode('rag');
	readonly setModeDirectAction = () => this.setMode('direct');
	readonly clearProjectAction = () => {
		this.selectedProjectId = undefined;
	};
	readonly backToProjectAction = () => this.backToProject();

	constructor(
		private chatApi: ChatApiService,
		private userService: UserInfoService,
		private chatSidecarService: ChatSidecarService,
		private s3FileService: S3FileService,
		private convState: ConversationStateService,
		private permissionService: PermissionService,
		public artifactState: ArtifactStateService
	) {
		this.openFreshSub = this.chatSidecarService.openFresh$.subscribe(() => {
			this.messages = [];
			this.conversationId = undefined;
			this.selectedProjectId = undefined;
			this.errorMessage = null;
			this.artifactState.closeArtifact();
		});
	}

	// ── Lifecycle hooks ──

	async ngOnInit(): Promise<void> {
		this.internalUserGUID = (await this.userService.getCurrentLoggedInUser()).internalUserGUID;
		this.hasDirectModePermission = await this.permissionService.userHasPermissionByKey('AiChat.WebSearch.View');
	}

	ngOnDestroy(): void {
		this.openFreshSub.unsubscribe();
	}

	// ── Computed getters ──

	/** Conversations list from the state service */
	get conversations(): ConversationSummary[] {
		return this.convState.conversations;
	}

	/** Projects list from the state service */
	get projects() {
		return this.convState.projects;
	}

	/** Whether mode toggle should be disabled (conversation already started) */
	get modeDisabled(): boolean {
		return this.messages.length > 0;
	}

	/** Display name of the currently selected project */
	get selectedProjectName(): string {
		return this.projects.find((p) => p.id === this.selectedProjectId)?.name ?? '';
	}

	/** True when the user is viewing a project overview (no active conversation yet) */
	get isInProjectOverview(): boolean {
		return this.isFullscreen && !!this.selectedProjectId && this.messages.length === 0;
	}

	/** Conversations belonging to the selected project, sorted by pin then recency */
	get projectConversations(): ConversationSummary[] {
		if (!this.selectedProjectId) return [];
		return this.conversations
			.filter((c) => c.projectId === this.selectedProjectId)
			.sort((a, b) => {
				if (a.pinned && !b.pinned) return -1;
				if (!a.pinned && b.pinned) return 1;
				return (b.timestamp || '').localeCompare(a.timestamp || '');
			});
	}

	// ── Public actions ──

	/** Open the sidecar panel */
	open(): void {
		this.openChange.emit(true);
	}

	/** Close the sidecar panel and exit fullscreen */
	close(): void {
		this.openChange.emit(false);
		this.fullscreenChange.emit(false);
	}

	/** Toggle between compact and fullscreen mode */
	toggleFullscreen(): void {
		const goingFullscreen = !this.isFullscreen;
		this.fullscreenChange.emit(goingFullscreen);
		if (goingFullscreen) {
			this.fullscreenSidebarOpen = true;
			this.loadHistory(false);
		}
	}

	/** Toggle the sidebar visibility in fullscreen mode */
	toggleFullscreenSidebar(): void {
		this.fullscreenSidebarOpen = !this.fullscreenSidebarOpen;
	}

	/** Switch chat mode (rag or direct). Only works before conversation starts. */
	setMode(mode: 'rag' | 'direct'): void {
		if (this.modeDisabled || mode === this.chatMode) return;
		this.chatMode = mode;
		this.messages = [];
		this.conversationId = undefined;
	}

	/** Reset state to start a fresh conversation */
	startNewChat(): void {
		this.messages = [];
		this.conversationId = undefined;
	}

	/** Navigate into a project's overview */
	selectProject(projectId: string): void {
		this.selectedProjectId = projectId;
		this.conversationId = undefined;
		this.messages = [];
	}

	/** Navigate back to project overview from a conversation within it */
	backToProject(): void {
		this.messages = [];
		this.conversationId = undefined;
	}

	/** Load a specific conversation by ID and display its messages */
	async selectConversation(id: string): Promise<void> {
		this.isLoading = true;
		try {
			const result = await this.convState.getConversationMessages(id);
			if (!result) {
				this.isLoading = false;
				return;
			}
			this.messages = result.messages;
			this.conversationId = result.conversationId;
			if (result.mode) this.chatMode = result.mode;
			// Defer isLoading=false by one tick so the flex layout settles before
			// any CDK overlay (tooltip) tries to read getBoundingClientRect().
			setTimeout(() => {
				this.isLoading = false;
			});
		} catch {
			this.isLoading = false;
		}
	}

	/** Delegate history loading to the conversation state service */
	loadHistory(force: boolean): void {
		this.convState.loadHistory(this.internalUserGUID ?? '', force);
	}

	// ── Context menu handlers ──

	/** Position and open the context menu for a project conversation */
	openConvMenu(event: MouseEvent, conv: ConversationSummary): void {
		event.preventDefault();
		event.stopPropagation();
		this.menuConvId = conv.id;
		this.menuConvPinned = !!conv.pinned;
		this.menuConvHasProject = !!conv.projectId;
		this.menuConvProjectId = conv.projectId ?? null;
		const panel = (event.target as HTMLElement).closest('.gs-sidecar-panel');
		const panelRect = panel?.getBoundingClientRect() ?? { left: 0, top: 0 };
		this.menuX = event.clientX - panelRect.left;
		this.menuY = event.clientY - panelRect.top;
	}

	/** Start renaming the context-menu-targeted conversation */
	onMenuRename(): void {
		if (!this.menuConvId) return;
		this.renameConv(this.menuConvId);
	}

	/** Move or remove the targeted conversation from a project */
	async onMenuMoveToProject(projectId: string | undefined): Promise<void> {
		if (!this.menuConvId) return;
		const convId = this.menuConvId;
		this.menuConvId = null;
		await this.convState.assignConversationToProject(convId, projectId, this.internalUserGUID ?? '');
	}

	/** Toggle pin on the targeted conversation */
	async onMenuTogglePin(): Promise<void> {
		if (!this.menuConvId) return;
		const convId = this.menuConvId;
		const pinned = this.menuConvPinned;
		this.menuConvId = null;
		await this.pinConv(convId, pinned);
	}

	/** Delete the targeted conversation. If it is the active conversation, reset to a fresh chat. */
	async onMenuDelete(): Promise<void> {
		if (!this.menuConvId) return;
		const convId = this.menuConvId;
		this.menuConvId = null;
		if (this.conversationId === convId) {
			this.startNewChat();
		}
		await this.deleteConv(convId);
	}

	/** Handle deletion from the fullscreen sidebar. Reset to fresh chat if it was the active conversation. */
	onSidebarConversationDeleted(convId: string): void {
		if (this.conversationId === convId) {
			this.startNewChat();
		}
	}

	/** Start renaming the context-menu-targeted conversation */
	private renameConv(convId: string): void {
		const conv = this.conversations.find((c) => c.id === convId);
		this.renameId = convId;
		this.renameVal = conv?.title ?? '';
	}

	/** Submit the rename and reload history */
	async submitRename(newTitle: string): Promise<void> {
		if (!this.renameId || !newTitle.trim()) {
			this.renameId = null;
			return;
		}
		const id = this.renameId;
		this.renameId = null;
		await this.convState.renameConversation(id, newTitle.trim(), this.internalUserGUID ?? '');
	}

	/** Toggle pin on the targeted conversation */
	private async pinConv(convId: string, currentlyPinned: boolean): Promise<void> {
		await this.convState.togglePinConversation(convId, currentlyPinned, this.internalUserGUID ?? '');
	}

	/** Delete the targeted conversation */
	private async deleteConv(convId: string): Promise<void> {
		await this.convState.deleteConversation(convId, this.internalUserGUID ?? '');
	}

	// ── Artifact panel orchestration ──

	/** Open a markdown artifact, transitioning to fullscreen if needed */
	onOpenArtifactContent(artifact: { title: string; content: string }): void {
		if (!this.isFullscreen) {
			this.fullscreenChange.emit(true);
			this.fullscreenSidebarOpen = false;
			this.loadHistory(false);
			setTimeout(() => this.artifactState.openArtifactContent(artifact.title, artifact.content), 300);
		} else {
			this.artifactState.openArtifactContent(artifact.title, artifact.content);
			this.fullscreenSidebarOpen = false;
		}
	}

	/** Open a document in the artifact panel, transitioning to fullscreen first if needed */
	async onViewDocument(event: { title: string; s3FileVersionGUID?: string }): Promise<void> {
		if (!this.isFullscreen) {
			this.fullscreenChange.emit(true);
			this.fullscreenSidebarOpen = false;
			this.loadHistory(false);
		}
		const error = await this.artifactState.openDocument(event);
		if (error) {
			this.errorMessage = error;
		} else {
			this.fullscreenSidebarOpen = false;
		}
	}

	// ── Messaging ──

	/** Handle a user message (with optional file attachment) from the chat input */
	onSendMessage(data: MessageWithFile): void {
		if (!data.message.trim() && !data.file) return;

		// Capture project context before any async work so assignment succeeds
		// even if component state changes mid-flight.
		const projectId = this.selectedProjectId;

		// When starting a new conversation inside a project, ensure conversationId is cleared.
		if (projectId && !this.conversationId) {
			this.messages = [];
			this.conversationId = undefined;
		}

		if (data.file && this.chatMode === 'rag') {
			this.chatMode = 'direct';
		}

		const userMsg: Message = {
			id: crypto.randomUUID(),
			conversationId: this.conversationId || '',
			role: 'user',
			content: data.file ? `${data.message} [Attached: ${data.file.name}]` : data.message,
			mode: this.chatMode,
			timestamp: new Date(),
		};
		this.messages = [...this.messages, userMsg];
		this.isLoading = true;
		this.errorMessage = null;

		if (data.file) {
			this.sendWithFile(data.message, data.file, projectId);
		} else {
			this.sendMessageOnly(data.message, projectId);
		}
	}

	// ── Private messaging helpers ──

	/** Send a text-only message and handle the response */
	private async sendMessageOnly(content: string, projectId?: string): Promise<void> {
		try {
			const res = await this.chatApi.sendMessage({ message: content, mode: this.chatMode, conversationId: this.conversationId, internalUserGUID: this.internalUserGUID ?? '' });
			if (!res) {
				this.isLoading = false;
				this.errorMessage = 'Failed to send message. Please try again.';
				return;
			}
			const isNew = !this.conversationId;
			this.conversationId = res.conversationId;
			this.handleChatResponse(res, isNew, projectId);
		} catch {
			this.isLoading = false;
			this.errorMessage = 'Failed to send message. Please try again.';
		}
	}

	/** Upload a file via S3, send the message referencing it, and handle the response */
	private async sendWithFile(content: string, file: File, projectId?: string): Promise<void> {
		try {
			const { s3FileGUID, s3FileVersionGUID } = await this.s3FileService.uploadS3FilePresignedURL({ file: file });
			const res = await this.chatApi.sendMessage({
				message: content,
				mode: this.chatMode,
				conversationId: this.conversationId,
				s3FileGUID: s3FileVersionGUID || s3FileGUID,
				fileName: file.name,
				internalUserGUID: this.internalUserGUID ?? '',
			});
			if (!res) {
				this.isLoading = false;
				this.errorMessage = 'Failed to upload document. Please try again.';
				return;
			}
			const isNew = !this.conversationId;
			this.conversationId = res.conversationId;
			this.handleChatResponse(res, isNew, projectId);
		} catch {
			this.isLoading = false;
			this.errorMessage = 'Failed to process document. Please try again.';
		}
	}

	/** Map API response sources to the local Source model */
	private mapResponseSources(rawSources: unknown): Source[] | undefined {
		type RawSource = { s3FileVersionGUID?: string; snippet?: string };
		const sourcesArray: RawSource[] = Array.isArray(rawSources) ? (rawSources as RawSource[]) : rawSources && typeof rawSources === 'object' ? [rawSources as RawSource] : [];
		const filtered = sourcesArray.filter((s) => s.s3FileVersionGUID);
		if (filtered.length === 0) return undefined;
		return filtered.map((s) => ({
			s3FileVersionGUID: s.s3FileVersionGUID!,
			excerpt: s.snippet ?? '',
		}));
	}

	/** Create the assistant message, trigger progressive rendering, and handle project assignment */
	private handleChatResponse(res: AiChatBotChatResponse, isNewConversation: boolean, projectId?: string): void {
		const assistantMsg: Message = {
			id: res.messageId,
			conversationId: res.conversationId,
			role: 'assistant',
			content: res.responseText,
			mode: this.chatMode,
			sources: this.mapResponseSources(res.sources),
			timestamp: new Date(),
			isProgressiveRendering: true,
		};
		this.messages = [...this.messages, assistantMsg];
		this.isLoading = false;

		setTimeout(() => {
			const components = this.messageComponents?.toArray();
			const lastComponent = components?.[components.length - 1];
			if (lastComponent) {
				lastComponent.startProgressiveRender(res.responseText);
			}
		});

		if (isNewConversation && projectId) {
			this.convState.assignConversationToProject(res.conversationId!, projectId, this.internalUserGUID ?? '').catch(() => this.loadHistory(true));
		} else if (this.isFullscreen) {
			this.loadHistory(true);
		}
	}
}
